import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Consent Categories
 * - necessary: Always true (required for site functionality)
 * - analytics: User analytics (GA4, etc.)
 * - marketing: Marketing scripts (GTM, ads, etc.)
 */
const CONSENT_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing'
};

/**
 * Default consent state (when no consent given yet)
 */
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null
};

/**
 * LocalStorage key for consent
 */
const CONSENT_STORAGE_KEY = 'user_consent_preferences';

/**
 * Load consent from localStorage
 * @returns {Object} Consent state
 */
const loadConsentFromStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return DEFAULT_CONSENT;
    }
    
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_CONSENT;
    }
    
    const parsed = JSON.parse(stored);
    
    // Validate structure
    if (typeof parsed !== 'object' || parsed === null) {
      return DEFAULT_CONSENT;
    }
    
    // Ensure necessary is always true
    return {
      necessary: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      timestamp: parsed.timestamp || null
    };
  } catch (error) {
    // Silent failure - return default
    return DEFAULT_CONSENT;
  }
};

/**
 * Save consent to localStorage
 * @param {Object} consent - Consent state
 */
const saveConsentToStorage = (consent) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    
    const toStore = {
      necessary: true, // Always true
      analytics: consent.analytics === true,
      marketing: consent.marketing === true,
      timestamp: Date.now()
    };
    
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    // Silent failure
  }
};

/**
 * Check if consent has been set (user has made a choice)
 * @param {Object} consent - Consent state
 * @returns {boolean}
 */
const hasConsentBeenSet = (consent) => {
  return consent.timestamp !== null;
};

// Create context
const ConsentContext = createContext(null);

/**
 * ConsentProvider component
 */
export const ConsentProvider = ({ children }) => {
  const [consent, setConsentState] = useState(DEFAULT_CONSENT);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load consent from storage on mount
  useEffect(() => {
    const storedConsent = loadConsentFromStorage();
    setConsentState(storedConsent);
    setIsLoaded(true);
  }, []);

  /**
   * Update consent state and save to storage
   */
  const setConsent = (updates) => {
    const newConsent = {
      ...consent,
      ...updates,
      necessary: true, // Always true
      timestamp: Date.now()
    };
    setConsentState(newConsent);
    saveConsentToStorage(newConsent);
  };

  /**
   * Accept all consent categories
   */
  const acceptAll = () => {
    setConsent({
      analytics: true,
      marketing: true
    });
  };

  /**
   * Reject all non-necessary consent categories
   */
  const rejectAll = () => {
    setConsent({
      analytics: false,
      marketing: false
    });
  };

  /**
   * Reset consent (clear storage)
   */
  const resetConsent = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(CONSENT_STORAGE_KEY);
      }
    } catch (error) {
      // Silent failure
    }
    setConsentState(DEFAULT_CONSENT);
  };

  /**
   * Check if a specific consent category is granted
   * @param {string} category - Consent category
   * @returns {boolean}
   */
  const hasConsent = (category) => {
    if (category === CONSENT_CATEGORIES.NECESSARY) {
      return true; // Always true
    }
    return consent[category] === true;
  };

  const value = {
    consent,
    isLoaded,
    hasConsentBeenSet: hasConsentBeenSet(consent),
    setConsent,
    acceptAll,
    rejectAll,
    resetConsent,
    hasConsent,
    CONSENT_CATEGORIES
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

/**
 * useConsent hook
 */
export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    // Fallback for components outside provider
    return {
      consent: DEFAULT_CONSENT,
      isLoaded: false,
      hasConsentBeenSet: false,
      setConsent: () => {},
      acceptAll: () => {},
      rejectAll: () => {},
      resetConsent: () => {},
      hasConsent: () => false,
      CONSENT_CATEGORIES
    };
  }
  return context;
};

export default ConsentContext;
